import React, {useRef} from "react";
import { Row, Container } from "react-bootstrap";
import styled from "styled-components";
import ReactTooltip from "react-tooltip";
import classNames from "classnames";
import { getImagePath } from "../../utilities/path";
import { getSettingAsObject, SettingsEnum } from '../../utilities/settings';
import useLanguage from "../../utilities/useLanguage";
import { getTranslation } from "./translations";
import { AutomationPlacement, createDataTestId, AutomationTestIdElementType } from '../../utilities/automation';
import { FaArrowRight, FaFacebook, FaYoutube, FaInstagram, FaLinkedin } from 'react-icons/fa'
import { FiCopy } from 'react-icons/fi';
import { RiTwitterXLine } from 'react-icons/ri';
import guidingForwardEnglish from "./guiding_forward.svg";
import guidingForwardSpanish from "./guiandole-hacia-el-futuro.svg";
import { VISIBLE_COMPONENTS_NAMES } from '../../context/VisibleComponentsContext';
import { useOnScreen } from '../Hooks/useOnScreen';
import { parseHTML } from "../../utilities/markdown";

const FooterItem = styled.a`
    font-size: 0.778rem;
    font-family: "National2";
    font-weight: 400;
    text-decoration: none;
    margin-bottom: 0.667rem;

    &:hover {
        text-decoration: underline;
    }
`;

const FooterSocialIcon = ({ url, Icon, title }) => {
    return (
        <a href={url} title={title} className='d-flex align-items-center' data-test-id={createDataTestId({placement: AutomationPlacement.SITE_FOOTER, element: AutomationTestIdElementType.LINK, specifier: 'social'})} rel="noopener noreferrer" target="_blank">
            <Icon color="#000" size={18} />
        </a>
    );
};

const DisclaimerIcon = styled.span<{$icon: { url: string, width: string, height: string }}>`
    font-size: 0;
    overflow: hidden;
    display: inline-block;
    background-image: url(${props => getImagePath(props.$icon.url)});
    background-position: 50%;
    background-repeat: no-repeat;
    width: ${props => props.$icon.width};
    height: ${props => props.$icon.height};
    margin-right: 10px;
`;

async function copyToClipboard (e, text) {
    e.preventDefault();

    try {
        await navigator.clipboard.writeText(text.replace(/\s/gi, ''));
    } catch (e) {
        console.error(`Failed to copy text ${text} to clipboard.`);
    }
}

const ServiceItem = ({header, label, href, allowCopy, translations}) => {
    const hasHeader = Boolean(header);
    const hasLink = Boolean(href);

    return (
        <div>
            {hasHeader &&
                <p className="footer-heading-small mb-0">{header}</p>
            }
            <div className="d-flex align-items-center">
                {hasLink ?
                    <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_FOOTER, element: AutomationTestIdElementType.LINK, specifier: 'service_item'})} className="text-decoration-none" href={href}><span className="service-text text-black">{label}</span></a>
                :
                    <span className="service-text text-black">{label}</span>
                }
                {allowCopy &&
                    <a onClick={(e) => copyToClipboard(e, label)} data-tip={translations.copied} data-event="click" data-event-off="click" data-place="right" data-iscapture={true} data-delay-hide={3000} className="copy-button" title={translations.copyToClipboard}>
                        <FiCopy color='#BDB6B4' size={24}/>
                    </a>
                }
            </div>
        </div>
    );
};

export default () => {
    const { lang } = useLanguage();
    const translations = getTranslation(lang);
    const settings = lang === 'es' ? getSettingAsObject(SettingsEnum.SITE_FOOTER_ES) : getSettingAsObject(SettingsEnum.SITE_FOOTER_EN);
    const guidingForwardImage = lang === 'es' ? guidingForwardSpanish : guidingForwardEnglish;
    const { appleStoreLink, googlePlayLink, footerLinksSection, disclaimerSection, serviceLinksSection,  } = settings;

    const footerRef = useRef(null);
    useOnScreen(footerRef, VISIBLE_COMPONENTS_NAMES.FOOTER)

    return (
        <div ref={footerRef} className="footer-container position-relative w-100 bg-pebble-20 mt-6" style={{zIndex: 5}}>
            <ReactTooltip className="footer-tooltip bg-white text-black" />
            <Container>
                <Row noGutters className="flex-reverse-md">
                    <div className="col-12 col-lg-auto d-flex flex-column">
                        <div className="service-links d-flex flex-wrap flex-column flex-lg-row">
                            {serviceLinksSection.map(({ header, label, href, allowCopy }, index) => (
                                <ServiceItem header={header} label={label} href={href} key={index} allowCopy={allowCopy} translations={translations}/>
                            ))}
                        </div>
                        {(appleStoreLink || googlePlayLink) &&
                            <div className="footer-app-buttons">
                                <p className="footer-heading-small">{translations.downloadOurApp}</p>
                                <div className="d-flex">
                                    {appleStoreLink &&
                                        <a className="footer btn btn-apple" title="App Store" href={appleStoreLink} rel="noopener noreferrer" target="_blank">
                                            App Store
                                        </a>
                                    }
                                    {googlePlayLink &&
                                        <a className="footer btn btn-google" title="Google Play" href={googlePlayLink} rel="noopener noreferrer" target="_blank">
                                            Google Play
                                        </a>
                                    }
                                </div>
                            </div>
                        }
                        <a data-test-id={createDataTestId({placement: AutomationPlacement.SITE_FOOTER, element: AutomationTestIdElementType.LINK, specifier: 'language_link'})} href={lang === 'es' ? "/" : '/es'} className="footer-language-link d-inline-block text-black">
                            <span className="d-inline-block mr-2">{translations.languageSiteLink}</span>
                            <FaArrowRight />
                        </a>
                    </div>
                    <div className="footer-links col-12 col-lg d-flex flex-column flex-sm-row justify-content-between">
                        {footerLinksSection.map((section, index) => (
                            <div className="d-flex flex-column" key={index}>
                                <span className="footer-heading text-redRock">{parseHTML(section.header, "footerHeader")}</span>
                                {section.links.map((link, index) => (
                                    <FooterItem data-test-id={createDataTestId({placement: AutomationPlacement.SITE_FOOTER, element: AutomationTestIdElementType.LINK})} key={index} href={link.href} className='text-black'>{parseHTML(link.label, "footerLink")}</FooterItem>
                                ))}
                            </div>
                        ))}
                    </div>
                </Row>
                <Row noGutters className="center-block">
                    <div className="col-12 col-lg-6 order-1 d-flex justify-content-end justify-content-lg-start">
                        <img loading="lazy" src={guidingForwardImage} alt={translations.guidingYouForward} className="footer-guiding-forward"/>
                    </div>
                    <div className="footer-social-icons col-12 col-lg-6 d-flex order-2 order-lg-1 align-items-end justify-content-start justify-content-lg-end pb-0 pb-lg-4 pt-4 pt-lg-0">
                        <FooterSocialIcon url="https://www.facebook.com/MountainAmerica/" Icon={FaFacebook} title='Facebook' />
                        <FooterSocialIcon url="https://www.youtube.com/user/mountainamerica" Icon={FaYoutube} title='Youtube'/>
                        <FooterSocialIcon url="https://www.instagram.com/mountainamericacu/" Icon={FaInstagram} title='Instagram'/>
                        <FooterSocialIcon url="https://x.com/MountainAmerica" Icon={RiTwitterXLine} title='X'/>
                        <FooterSocialIcon url="https://www.linkedin.com/company/mountain-america-credit-union/" Icon={FaLinkedin} title='LinkedIn'/>
                    </div>
                    <div className='col-12 order-1 footer-divider w-100 h-100'></div>
                </Row>
                <Row noGutters>
                    <div className="col-12 disclaimer-container">
                            {disclaimerSection.disclaimers.map((disclaimerText, index) => {
                                const disclaimerMarkup = parseHTML(disclaimerText, "disclaimerText");
                                return (
                                    <div key={index} className="disclaimer-text text-pebble">
                                        {disclaimerMarkup}
                                    </div>
                                );
                            })}
                        </div>
                    <div className="col-12 external-links-container d-flex flex-column flex-sm-row flex-wrap">
                        {disclaimerSection.links.map(({label, href, title, icon, target}, index) => {
                            const hasIcon = Boolean(icon);
                            const hasLink = Boolean(href);

                            if (hasLink) {
                                return (
                                    <a key={index} className={classNames('d-inline-flex align-items-center container-disclaimer text-pebble', {'text-decoration-none': hasIcon})} href={href} title={title} target={target ? target : '_self'} rel="noopener noreferrer" data-test-id={createDataTestId({placement: AutomationPlacement.SITE_FOOTER, element: AutomationTestIdElementType.LINK, specifier: 'disclaimer'})}>
                                        {hasIcon && <DisclaimerIcon $icon={icon}>{icon.alt || ''}</DisclaimerIcon>}
                                        <small>{parseHTML(label, "footerLink")}</small>
                                    </a>
                                );
                            }

                            return (
                                <span key={index} className='d-inline-flex align-items-center text-pebble text-underline'>
                                    <small>{parseHTML(label, "footerLabel")}</small>
                                </span>
                            );
                        })}
                    </div>
                </Row>
            </Container>
        </div>
    );
};